import XLSX from 'xlsx'

/**
 * cx.ExpExcel
 * 数据报表，导出Excel文件
 *
 * @class
 * @memberof    cx
 * @author      Gao Xulong
 * @since       2018-07-18
 */
class ExpExcel
{
  /**
	 * 单元格Style样式使用说明。
	 * @typedef	    {Object}	      ExcelStyle
   * @property    {Object}        fill                    填充样式
   * @property    {Object}        fill.patternType        图表类型(可取值： 'solid' or 'none')
   * @property    {COLOR_SPEC}    fill.fgColor            填充颜色
   * @property    {COLOR_SPEC}    fill.bgColor            边框颜色
   * @property    {Object}        font                    字体样式
   * @property    {String}        font.name               单元格内容的字体(如'宋体')
   * @property    {Number}        font.sz                 字体大小
   * @property    {COLOR_SPEC}    font.color              字体颜色
   * @property    {Boolean}       font.bold               是否加粗（默认false）
   * @property    {Boolean}       font.underline          是否加下划线（默认false）
   * @property    {Boolean}       font.italic             是否斜体（默认false）
   * @property    {Boolean}       font.strike             字体中间是否加入横划线（默认false）
   * @property    {Boolean}       font.outline            是否显示轮廓线（默认false）
   * @property    {Boolean}       font.shadow             是否有阴影（暂时未看出具体效果）
   * @property    {Boolean}       font.vertAlign          是否强制不准自动换行(默认false）
   * @property    {Object}        alignment                对齐样式
   * @property    {String}        alignment.vertical       单元格内字体垂直方向对齐方式。（可取值:"bottom"||"center"||"top" ,默认bottom,下对齐）
   * @property    {String}        alignment.horizontal     单元格内字体水平方向对齐方式。（可取值:"bottom"||"center"||"top" ,默认bottom,左对齐）
   * @property    {Boolean}       alignment.wrapText       单元格内容是否自动换行。（默认为false）
   * @property    {Number}        alignment.readingOrder   指定内容的阅读顺序。(可取值：2（for right-to-left）...,具体用法暂不清楚)
   * @property    {Boolean}       alignment.textRotation   单元格内容旋转角度。（取值范围（0~255）,默认角度为0）
   * @property    {Object}        border                   边框样式
   * @property    {Object}        border.top               上边框的样式
   * @property    {COLOR_SPEC}    border.top.color         上边框的线条颜色
   * @property    {BORDER_STYLE}  border.top.style         上边框的线条样式
   * @property    {Object}        border.bottom            下边框的样式
   * @property    {COLOR_SPEC}    border.bottom.color      下边框的线条颜色
   * @property    {BORDER_STYLE}  border.bottom.style      下边框的线条样式
   * @property    {Object}        border.left              左边框的样式
   * @property    {COLOR_SPEC}    border.left.color        左边框的线条颜色
   * @property    {BORDER_STYLE}  border.left.style        左边框的线条样式
   * @property    {Object}        border.right             右边框的样式
   * @property    {COLOR_SPEC}    border.right.color       右边框的线条颜色
   * @property    {BORDER_STYLE}  border.right.style       右边框的线条样式
   * @property    {Object}        border.diagonal          对角线的样式
   * @property    {COLOR_SPEC}    border.diagonal.color    对角线的线条颜色
   * @property    {BORDER_STYLE}  border.diagonal.style    对角线的线条样式
   * @property    {Boolean}       border.diagonalUp        具体用法暂不清楚
   * @property    {Boolean}       border.diagonalDown      具体用法暂不清楚
   * @property    {String}        numFmt                  格式化单元格内的数据（各种取值对应效果如下：）
   * "0"-----------------------------------> 将单元格内容格式化成数字格式
   * "0.00%"----------------------------> 将单元格内容格式化成百分数格式
   * "0.0%"------------------------------> (暂时不清楚用法)
   * "0.00%;\\(0.00%\\);\\-;@"---------> 将单元格内容指定自定义格式的字符串，可避开特殊字符
   * "m/dd/yy"--------------------------> 使用Excel格式表示字符串格式的日期格式
   */

  /**
   * @typedef     {Object}      COLOR_SPEC    为fill对象、font对象和border对象提供颜色样式
   * @property    {Number}      auto          指定自动值
   * @property    {Number}      theme         指定主题颜色
   * @property    {Number}      indexed       等于64时，就是fill.bgColor的默认值
   * @property    {Number}      tint          色调值的整数索引(默认值为0)
   */
  /**
   * @typedef     {String}      BORDER_STYLE  边框样式是一个字符串值，可以采用下列值之一
   * 'thin'------------------------>(效果暂未知)
   * 'medium'---------------------->(效果暂未知)
   * 'thick'----------------------->(效果暂未知)
   * 'dotted'---------------------->(效果暂未知)
   * 'hair'------------------------>(效果暂未知)
   * 'dashed'---------------------->(效果暂未知)
   * 'mediumDashed'---------------->(效果暂未知)
   * 'dashDot'--------------------->(效果暂未知)
   * 'mediumDashDot'--------------->(效果暂未知)
   * 'dashDotDot'------------------>(效果暂未知)
   * 'mediumDashDotDot'------------>长短不一的虚线
   * 'slantDashDot'---------------->(效果暂未知)
   */

  /**
   * json数据导出为Excel文件。
   * @static
   * @method
   * @param {Object}        param                         参数对象
   * @param {Array.Object}  param.json                    待导出的json数据
   * @param {Object}        param.columns                 列字段及别名的map集合。格式：{column1: column1title, column2: column2title, ...}
   * @param {Object}        [param.title]                 主标题。不传的话不显示。
   * @param {String}        [param.filename = 'mySheet']  待导出的文件的文件名
   * @param {Object}        [param.style]                 exce文件的主标题、列标题、单元格等样式。可传可不传。
   * @param {ExcelStyle}    [param.style.main]            主标题样式。格式为{fill:{}, font:{}, alignment:{}, border:{}}
   * @param {Object}        [param.style.main.fill]       主标题填充样式
   * @param {Object}        [param.style.main.font]       主标题字体样式
   * @param {Object}        [param.style.main.alignment]  主标题对齐样式
   * @param {Object}        [param.style.main.border]     主标题边框样式
   * @param {ExcelStyle}    [param.style.head]            列标题样式。（内部格式同上）
   * @param {ExcelStyle}    [param.style.comm]            普通单元格样式。（内部格式同上）
   */
  static exportToExcel(param)
  {
    //1、初始化变量
    let filename = (param.filename === undefined || param.filename === '') ? 'mySheet' : param.filename   //sheet页名
    let sheetName = param.sheetName || filename    //sheet页名，如果传入的有，用传入的；否则，用文件名前30位。
    //对文件名进行过滤，过滤非法字符  / \ " : | * ? < >
    filename = this.fillterFileName(filename)
    sheetName = this.fillterFileName(sheetName)
    let json = JSON.parse(JSON.stringify(param.json))                     //json源数据
    let style = this.setStyle(param)          //构造的主标题、列标题、单元格等样式
    let columns = param.columns               //列字段及别名的map集合
    let downloadName = filename + '.xlsx'     //最终下载的文件名(带格式后缀)。
    let keyMap = []                           //excel列的keys集合。
    let tmpdata = []                          //用来保存转换好的json
    json.unshift({})                           //空出一行来存放列标题
    //2、构造列标题集合。
    for (let key in columns)
    {
      keyMap.push(key)
      json[0][key] = columns[key]
    }
    //3、如果传入主标题则空出两行来显示
    if (param.title)
    {
      json.unshift({})
      json.unshift({})                           //空出两行来存放主标题
    }
    //4、遍历数据源json构造excel数据。
    json.map((v, i) =>
      keyMap.map((k, j) =>
        Object.assign({}, {
          v: v[k] === undefined ? '' : v[k],
          position: (j > 25 ? this.getCharCol(j) : String.fromCharCode(65 + j)) + (i + 1)
        }
        )
      )
    ).reduce((prev, next) => prev.concat(next))
      .forEach((v, i) =>
      {
        tmpdata[v.position] =
          {
            v: v.v, //单元格内容
            s: style.comm //单元格样式
          }
        if (v.v && typeof (v.v) === 'string' && v.v.indexOf('错误:') !== -1)
        {
          tmpdata[v.position].s = Object.assign({}, style.comm,
            {
              font: {
                color: { rgb: 'FFFFAA00' }
              }
            })
        }
      })
    //5、设置数据填充区域以及Sheet页名称。
    let outputPos = Object.keys(tmpdata)        //设置区域,比如表格从A1到D10
    let SheetNames = []
    let Sheets = {}
    SheetNames.push(sheetName)
    Sheets[sheetName] = Object.assign(
      {},
      tmpdata, //内容
      { '!ref': outputPos[0] + ':' + outputPos[outputPos.length - 1] } //设置填充区域
    )
    let tmpWB = {
      SheetNames: SheetNames, //保存的表标题
      Sheets: Sheets
    }
    //6、针对主标题进行自定义合并以及样式的自定义设定。
    let dataInfo = tmpWB.Sheets[tmpWB.SheetNames[0]]
    if (param.title)
    {
      //设置合并方式。存的是一个对象数组，每个对象标识一个合并方式。其中s:{c:0, r:0}和 e: {c: keyMap.length - 1, r: 1}分别表示开始的单元格位置和结束的单元格位置，这两个位置的对角线所属矩形范围都将合并成一个单元格，
      //其中 c是列序号，r是行序号；
      dataInfo['!merges'] = [{s: {c: 0, r: 0}, e: {c: keyMap.length - 1, r: 1}}]
      dataInfo['A1'].v = sheetName               //设置表格标题         //设置主标题样式
      dataInfo['A1'].s = style.main
    }
    //7、自定义设置列标题的样式
    dataInfo[param.title ? 'A3' : 'A1'].s = style.head
    for (let i = 1; i < keyMap.length; i++)
      dataInfo[this.getCharCol(i) + (param.title ? '3' : '1')].s = style.head
    //8、创建二进制对象写入转换好的字节流
    let tmpDown = new Blob(
      [this.stringToArrayBuffer(XLSX.write(
        tmpWB,
        { bookType: 'xlsx', bookSST: false, type: 'binary' }//这里的数据是用来定义导出的格式类型
      ))],
      { type: '' }
    )
    //9、创建链接并下载文件
    let href = URL.createObjectURL(tmpDown)         //创建对象超链接
    this.downloadFile(href, downloadName)           //下载文件
    setTimeout(() =>              //延时释放
    {
      // URL.revokeObjectURL(tmpDown)    //用URL.revokeObjectURL()来释放这个object URL
    }, 100)
  }

  static downloadFile(url, filename)
  {
    let domA = document.createElement('a')    //创建一个a标签,用做后面的模拟点击下载操作。
    domA.href = url              //绑定a标签的下载地址
    domA.download = filename  //绑定a标签的下载文件名
    domA.click()                  //模拟点击实现下载
  }

  /**
   * 过滤掉名称中的特殊字符   / \ " : | * ? < >
   * @static
   * @method
   * @returns {fileName}  名称字符串
   */
  static fillterFileName(fileName)
  {
    return fileName.replace(/\\/g, '').replace(/\//g, '').replace(/"/g, '').replace(/:/g, '').replace(/\|/g, '').replace(/\*/g, '').replace(/\?/g, '').replace(/</g, '').replace(/>/g, '').substring(0, 30)
  }

  /**
   * 将指定的自然数转换为26进制表示。映射关系：[0-25] -> [A-Z]。
   * @static
   * @method
   * @param   {Number}  num 自然数
   * @returns {String}  自然数转成的字母
   */
  static getCharCol(num)
  {
    let s = ''
    let m = 0
    while (num > 0)
    {
      m = num % 26 + 1
      s = String.fromCharCode(m + 64) + s
      num = (num - m) / 26
    }
    return s
  }

  static setStyle(param)
  {
    let style = {
      main:
      {
        font: {
          name: '宋体',
          sz: 20,
          color: { rgb: 'FFFFAA00' },
          bold: true
        },
        alignment: {
          horizontal: 'center',
          vertical: 'center',
          wrapText: true
        }
      },
      head:
      {
        font: {
          name: '宋体',
          sz: 14,
          bold: true
        },
        alignment: {
          horizontal: 'center',
          vertical: 'center',
          wrapText: true
        }
      },
      comm:
      {
        alignment: {
          vertical: 'center',
          horizontal: 'center',
          wrapText: true
        }
      }
    }
    style.main = (param.style && param.style.main) ? param.style.main : style.main
    style.head = (param.style && param.style.head) ? param.style.head : style.head
    style.comm = (param.style && param.style.comm) ? param.style.comm : style.comm
    return style
  }

  /**
   * 字符串转字符流
   * @static
   * @method
   * @param   {String}  str 待转成字符流的字符串
   * @returns {ArrayBuffer} 字符流
   */
  static stringToArrayBuffer(str)
  {
    let buf = new ArrayBuffer(str.length)
    let view = new Uint8Array(buf)
    for (let i = 0; i !== str.length; ++i)
      view[i] = str.charCodeAt(i) & 0xFF
    return buf
  }

  /**
   * 导出多sheet页
   * @static
   * @method
   * @param {Object}        param                         参数对象
   * @param {Object.Array}  param.json                    待导出的json数据。格式{'tab1': [{},{}], 'tab2': [{},{}] };
   * @param {Object.Object} param.columns                 列字段及别名的map集合。格式：{ 'tab1': {column1: column1title, column2: column2title, ...}, 'tab2': {column1: column1title, column2: column2title, ...}}
   * @param {Object}        [param.sheets]                sheet页显示名。格式{'tab1': 名字, 'tab2': 名字 };不传默认显示表名
   * @param {String}        [param.filename = 'mySheet']  待导出的文件的文件名
   */
  static exportToSheets(param)
  {
    //1、初始化变量
    let filename = (param.filename === undefined || param.filename === '') ? 'mySheet' : param.filename   //sheet页名
    filename = this.fillterFileName(filename)
    let json = JSON.parse(JSON.stringify(param.json))                     //json源数据
    let downloadName = filename + '.xlsx'     //最终下载的文件名(带格式后缀)。

    let sheetNames = []
    let sheetsList = {}
    const wb = XLSX.utils.book_new()

    for (let key in json)
    {
      let sheetName = param.sheets ? param.sheets[key] : key
      sheetNames.push(sheetName)

      let columns = param.columns[key] // 此处是每个sheet的表头
      
      let content = []
      let columnHeader = []

      for (let i in columns)
        columnHeader.push(columns[i])
      content.push(columnHeader)

      json[key].forEach((item, index) =>
      {
        let arr = []
        for (let i in columns)
          arr.push(item[i])
        content.push(arr)
      })
      sheetsList[sheetName] = XLSX.utils.aoa_to_sheet(content)
    }

    wb['SheetNames'] = sheetNames
    wb['Sheets'] = sheetsList

    XLSX.writeFile(wb, downloadName)
  }
}

export default ExpExcel



// WEBPACK FOOTER //
// ./src/lib/ExpExcel.js